<template>
  <v-card class="my-3">
    <v-card-text>
      <svg
        :viewBox="`0 0 640 ${svgHeight}`"
        width="100%"
        :height="svgHeight"
        style="max-width: 640px; display: block; margin: 0 auto"
      >
        <!-- 基準局 → caster -->
        <line x1="140" :y1="centerY" x2="250" :y2="centerY" :stroke="baseLink.color" stroke-width="4" />
        <text x="195" :y="centerY - 10" text-anchor="middle" font-size="11" :fill="baseLink.color">
          {{ baseLink.label }}
        </text>

        <!-- 基準局ボックス -->
        <rect
          x="20"
          :y="centerY - 25"
          width="120"
          height="50"
          rx="8"
          fill="none"
          :stroke="baseLink.color"
          stroke-width="2"
        />
        <text x="80" :y="centerY - 3" text-anchor="middle" font-size="13" fill="currentColor">
          {{ $tt('caster.baseShort') }}
        </text>
        <text x="80" :y="centerY + 14" text-anchor="middle" font-size="10" fill="gray">
          {{ mountpoint }}
        </text>

        <!-- casterボックス -->
        <rect x="250" :y="centerY - 25" width="140" height="50" rx="8" fill="none" stroke="#607D8B" stroke-width="2" />
        <text x="320" :y="centerY - 3" text-anchor="middle" font-size="13" fill="currentColor">
          {{ $tt('caster.casterServer') }}
        </text>
        <text x="320" :y="centerY + 14" text-anchor="middle" font-size="10" fill="gray">AgriBus Caster</text>

        <!-- caster → 移動局 -->
        <g v-for="(rover, index) in roverNodes" :key="rover.id">
          <line x1="390" :y1="centerY" x2="500" :y2="30 + index * rowHeight" :stroke="rover.color" stroke-width="3" />
          <rect
            x="500"
            :y="10 + index * rowHeight"
            width="130"
            height="40"
            rx="8"
            fill="none"
            :stroke="rover.color"
            stroke-width="2"
          />
          <text x="565" :y="27 + index * rowHeight" text-anchor="middle" font-size="12" fill="currentColor">
            {{ rover.name }}
          </text>
          <text x="565" :y="42 + index * rowHeight" text-anchor="middle" font-size="9" fill="gray">
            {{ rover.sub }}
          </text>
        </g>
        <g v-if="roverNodes.length === 0">
          <line
            x1="390"
            :y1="centerY"
            x2="500"
            :y2="centerY"
            stroke="#9E9E9E"
            stroke-width="2"
            stroke-dasharray="6 4"
          />
          <rect
            x="500"
            :y="centerY - 20"
            width="130"
            height="40"
            rx="8"
            fill="none"
            stroke="#9E9E9E"
            stroke-width="2"
            stroke-dasharray="6 4"
          />
          <text x="565" :y="centerY + 4" text-anchor="middle" font-size="11" fill="gray">
            {{ $tt('caster.roverShort') }}: 0
          </text>
        </g>
      </svg>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from 'moment-timezone'
import i18n from '@/lib/i18n'

const COLOR_OK = '#4CAF50'
const COLOR_STALE = '#FF9800'
const COLOR_DOWN = '#F44336'
const STALE_MS = 15 * 1000
const DOWN_MS = 60 * 1000

/**
 * 基準局 → Caster → 移動局 の接続経路図。
 * 各区間を最終受信時刻に応じて 緑(正常)/橙(停滞)/赤(切断) で色分けする。
 */
export default {
  name: 'CasterPathDiagram',
  props: {
    stats: { type: Object, default: null }, // MountpointStats { current, lastEnded }
    track: { type: Object, default: null }, // MountpointTrack { rovers }
  },
  computed: {
    mountpoint() {
      return (this.stats && this.stats.mountpoint) || ''
    },
    baseLink() {
      const current = this.stats && this.stats.current
      if (!current) {
        const endedAt = this.stats && this.stats.lastEnded && this.stats.lastEnded.endedAt
        const label = endedAt
          ? `${i18n.tt('caster.disconnectedAt')} ${moment(endedAt).format('MM/DD HH:mm')}`
          : i18n.tt('caster.noConnection')
        return { color: COLOR_DOWN, label }
      }
      const lastSeen = this.latestMessageAt(current)
      const age = Date.now() - lastSeen
      if (age > DOWN_MS) {
        return {
          color: COLOR_DOWN,
          label: `${i18n.tt('caster.lastReceivedAt')} ${moment(lastSeen).format('HH:mm:ss')}`,
        }
      }
      if (age > STALE_MS) {
        return { color: COLOR_STALE, label: i18n.tt('caster.stale') }
      }
      return { color: COLOR_OK, label: `${i18n.tt('caster.receiving')} ${this.formatRate(current)}` }
    },
    roverNodes() {
      const rovers = (this.track && this.track.rovers) || []
      return rovers
        .filter(rover => !rover.endedAt)
        .slice(0, 6)
        .map(rover => {
          const lastSeen = rover.stats && rover.stats.lastSeenAt
          let color = COLOR_OK
          let sub = i18n.tt('caster.receiving')
          if (!lastSeen) {
            color = COLOR_STALE
            sub = 'GGA: --'
          } else if (Date.now() - lastSeen > DOWN_MS) {
            color = COLOR_STALE
            sub = `GGA ${moment(lastSeen).format('HH:mm:ss')}`
          } else {
            const point = rover.stats.lastPoint
            sub =
              point && point.baselineM !== undefined
                ? `${i18n.tt('caster.baseline')} ${Math.round(point.baselineM)}m`
                : 'GGA OK'
          }
          return {
            id: rover.roverId,
            name: rover.clientId || this.$tt('caster.roverShort'),
            sub,
            color,
          }
        })
    },
    rowHeight() {
      return 55
    },
    svgHeight() {
      const rows = Math.max(this.roverNodes.length, 1)
      return Math.max(100, 20 + rows * this.rowHeight)
    },
    centerY() {
      return this.svgHeight / 2
    },
  },
  methods: {
    latestMessageAt(snapshot) {
      let latest = 0
      const types = snapshot.messageTypes || {}
      Object.keys(types).forEach(type => {
        if (types[type].lastSeenAt > latest) {
          latest = types[type].lastSeenAt
        }
      })
      return latest || snapshot.collectedAt
    },
    formatRate(snapshot) {
      if (!snapshot || snapshot.bytesInPerSecond === undefined) {
        return ''
      }
      return `${Math.round(snapshot.bytesInPerSecond)} B/s`
    },
  },
}
</script>
